import { useCallback, useEffect, useMemo, useState } from "react";
import { BUCKETS, BUCKET_LABELS, STATUS_LABELS, type Bucket, type Person, type Project } from "../../shared/types.ts";
import { api } from "../api.ts";
import { AreaBadge, Banner, Empty, Loading, SegmentedControl, StatusBadge } from "../components.tsx";
import {
  ALL_AREAS,
  DEFAULT_FILTERS,
  LIST_STATUSES,
  activeFilterCount,
  filterProjects,
  filtersFromSearch,
  filtersToSearch,
  isFiltering,
  type ListFilters,
  type StatusFilter,
} from "../filters.ts";
import { moveItem } from "../order.ts";
import { currentPath, navigate } from "../useRoute.ts";

const STATUS_FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  ...LIST_STATUSES.map((status) => ({ value: status, label: STATUS_LABELS[status] })),
];

/**
 * Substitutes a bucket's projects, in `newIds` order, back into the full
 * list without disturbing any other bucket's relative order. Used for the
 * optimistic UI update while the reorder request is in flight.
 */
function withBucketOrder(projects: Project[], bucket: Bucket, newIds: string[]): Project[] {
  const byId = new Map(projects.map((p) => [p.id, p]));
  let i = 0;
  return projects.map((p) => (p.bucket === bucket ? byId.get(newIds[i++]!)! : p));
}

/** Replaces the current hash's query string in place — no new history entry per keystroke. */
function syncFiltersToUrl(filters: ListFilters) {
  const path = currentPath().split("?")[0] || "/";
  const next = `#${path}${filtersToSearch(filters)}`;
  if (window.location.hash !== next) {
    window.history.replaceState(null, "", next);
  }
}

export function ProjectList({ person }: { person: Person }) {
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [areas, setAreas] = useState<string[]>([]);
  const [filters, setFilters] = useState<ListFilters>(() => filtersFromSearch(currentPath().split("?")[1] ?? ""));
  const [filterOpen, setFilterOpen] = useState(() => isFiltering(filters));

  // The first (and only) role-based permission in the app: kids see the
  // shared order but can't change it.
  const canReorder = person.role === "adult";

  const load = useCallback(async () => {
    try {
      setProjects(await api.listProjects());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load projects");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    api
      .areas()
      .then(setAreas)
      .catch(() => {
        // The area chip row is a nicety, not load-bearing — a failed fetch
        // just means fewer chips, not a broken page.
      });
  }, []);

  useEffect(() => {
    syncFiltersToUrl(filters);
  }, [filters]);

  const clearFilters = () => setFilters(DEFAULT_FILTERS);

  async function reorder(bucket: Bucket, from: number, to: number) {
    if (!projects) return;
    const ids = projects.filter((p) => p.bucket === bucket).map((p) => p.id);
    const nextIds = moveItem(ids, from, to);
    if (nextIds === ids) return;

    const previous = projects;
    setProjects(withBucketOrder(projects, bucket, nextIds));
    try {
      await api.reorderProjects(bucket, nextIds);
    } catch (err) {
      setProjects(previous);
      setError(err instanceof Error ? err.message : "Could not save the new order");
    }
  }

  const filtered = useMemo(() => (projects ? filterProjects(projects, filters) : []), [projects, filters]);
  const filtering = isFiltering(filters);
  const activeCount = activeFilterCount(filters);
  // Dragging a partial (filtered) list would corrupt the shared order, so
  // reordering is disabled while any filter or search is narrowing the view.
  const reorderEnabled = canReorder && !filtering;

  if (error && !projects) return <Banner kind="error">{error}</Banner>;
  if (!projects) return <Loading />;
  if (projects.length === 0) {
    return (
      <Empty>
        Nothing on the list. <a href="#/add">Add the first project →</a> or{" "}
        <a href="#/bulk-add">bulk add a whole area →</a>
      </Empty>
    );
  }

  return (
    <div className="project-list">
      <div className="row between">
        <span className="muted small">Projects</span>
        <a href="#/bulk-add">＋ Bulk add</a>
      </div>
      {error && <Banner kind="error">{error}</Banner>}

      <FilterBar
        filters={filters}
        onChange={setFilters}
        onClear={clearFilters}
        areas={areas}
        open={filterOpen}
        onToggleOpen={() => setFilterOpen((open) => !open)}
        activeCount={activeCount}
      />

      {filtering && filtered.length === 0 ? (
        <Empty>
          Nothing matches those filters.{" "}
          <button type="button" className="link" onClick={clearFilters}>
            Clear filters
          </button>
        </Empty>
      ) : (
        BUCKETS.map((bucket) => {
          const bucketProjects = filtered.filter((p) => p.bucket === bucket);
          if (bucketProjects.length === 0) return null;
          const order = bucketProjects.map((p) => p.id);

          return (
            <section key={bucket} className="bucket">
              <header className="bucket-header">
                <h2>
                  {BUCKET_LABELS[bucket]} <span className="count">{bucketProjects.length}</span>
                </h2>
              </header>

              <ol className="ranked">
                {bucketProjects.map((project, index) => (
                  <RankRow
                    key={project.id}
                    index={index}
                    total={bucketProjects.length}
                    project={project}
                    order={order}
                    canReorder={reorderEnabled}
                    onMove={(from, to) => reorder(bucket, from, to)}
                  />
                ))}
              </ol>
            </section>
          );
        })
      )}

      <p className="muted small pad">
        {filtering
          ? canReorder
            ? "Clear filters to reorder — dragging a partial list could corrupt the order."
            : "This is the family's shared order."
          : canReorder
            ? "Drag or use ▲▼ to set the order — everyone in the family sees it."
            : "This is the family's shared order."}
      </p>
    </div>
  );
}

interface FilterBarProps {
  filters: ListFilters;
  onChange: (filters: ListFilters) => void;
  onClear: () => void;
  areas: string[];
  open: boolean;
  onToggleOpen: () => void;
  activeCount: number;
}

/**
 * Status + area + live search, collapsed behind a toggle so it doesn't eat
 * the screen on a phone. The toggle carries a count badge whenever a filter
 * is active, so it's obvious there's something to clear even while closed.
 */
function FilterBar({ filters, onChange, onClear, areas, open, onToggleOpen, activeCount }: FilterBarProps) {
  return (
    <div className="filter-bar">
      <div className="row between">
        <button
          type="button"
          className={`secondary filter-toggle ${activeCount > 0 ? "filter-toggle-active" : ""}`}
          aria-expanded={open}
          onClick={onToggleOpen}
        >
          ⚲ Filter{activeCount > 0 && <span className="count-badge">{activeCount}</span>}
        </button>
        {activeCount > 0 && (
          <button type="button" className="link" onClick={onClear}>
            Clear
          </button>
        )}
      </div>

      {open && (
        <div className="filter-panel">
          <SegmentedControl
            legend="Status"
            options={STATUS_FILTER_OPTIONS}
            value={filters.status}
            onChange={(status) => onChange({ ...filters, status })}
          />

          <div className="chip-row" role="group" aria-label="Area">
            <button
              type="button"
              className={`chip ${filters.area === ALL_AREAS ? "chip-on" : ""}`}
              onClick={() => onChange({ ...filters, area: ALL_AREAS })}
            >
              All areas
            </button>
            {areas.map((area) => (
              <button
                key={area}
                type="button"
                className={`chip ${filters.area === area ? "chip-on" : ""}`}
                onClick={() => onChange({ ...filters, area: filters.area === area ? ALL_AREAS : area })}
              >
                {area}
              </button>
            ))}
          </div>

          <input
            aria-label="Search projects"
            value={filters.q}
            onChange={(e) => onChange({ ...filters, q: e.target.value })}
            placeholder="Search title, area, notes…"
          />
        </div>
      )}
    </div>
  );
}

interface RankRowProps {
  project: Project;
  index: number;
  total: number;
  order: string[];
  canReorder: boolean;
  onMove: (from: number, to: number) => void;
}

function RankRow({ project, index, total, order, canReorder, onMove }: RankRowProps) {
  const [dragging, setDragging] = useState(false);

  // Warn when this project sits above something it's waiting on, within the
  // same bucket. (A blocker in an earlier bucket is already scheduled
  // sooner in the shared order, so it can never trip this; a blocker in a
  // later bucket isn't "same-or-earlier" and is left alone.)
  const blockersBelow = project.blocked_by.filter((blocker) => {
    if (blocker.status === "done") return false;
    const blockerIndex = order.indexOf(blocker.id);
    return blockerIndex > index;
  });

  const unfinishedBlockers = project.blocked_by.filter((b) => b.status !== "done");

  return (
    <li
      className={`rank-row ${dragging ? "dragging" : ""}`}
      draggable={canReorder}
      onDragStart={(e) => {
        if (!canReorder) return;
        setDragging(true);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", String(index));
      }}
      onDragEnd={() => setDragging(false)}
      onDragOver={(e) => {
        if (canReorder) e.preventDefault();
      }}
      onDrop={(e) => {
        if (!canReorder) return;
        e.preventDefault();
        const from = Number(e.dataTransfer.getData("text/plain"));
        if (Number.isInteger(from)) onMove(from, index);
      }}
    >
      <span className="rank-number" aria-hidden="true">
        {index + 1}
      </span>

      <button className="rank-body" onClick={() => navigate(`/p/${project.id}`)}>
        <span className="rank-title">{project.title}</span>
        <span className="badges">
          <StatusBadge status={project.status} />
          <AreaBadge area={project.area} />
          {unfinishedBlockers.length > 0 && (
            <span className="badge blocked">⛔ blocked by {unfinishedBlockers.map((b) => b.title).join(", ")}</span>
          )}
          {project.photo_count > 0 && <span className="badge quiet">📷 {project.photo_count}</span>}
          {project.comment_count > 0 && <span className="badge quiet">💬 {project.comment_count}</span>}
        </span>
        {blockersBelow.length > 0 && (
          <span className="warn-line">
            ⚠ Ranked above {blockersBelow.length === 1 ? "its blocker" : "its blockers"}:{" "}
            {blockersBelow.map((b) => b.title).join(", ")}
          </span>
        )}
      </button>

      {canReorder && (
        <span className="rank-controls">
          <button aria-label="Move up" disabled={index === 0} onClick={() => onMove(index, index - 1)}>
            ▲
          </button>
          <button
            aria-label="Move down"
            disabled={index === total - 1}
            onClick={() => onMove(index, index + 1)}
          >
            ▼
          </button>
        </span>
      )}
    </li>
  );
}
